import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Wind, ChevronDown } from 'lucide-react';
import logger from '@/utils/logger';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAqiColor(aqi) {
  if (aqi <= 50) return { main: '#10B981', glow: 'rgba(16,185,129,0.3)', label: 'Good' };
  if (aqi <= 100) return { main: '#F59E0B', glow: 'rgba(245,158,11,0.3)', label: 'Moderate' };
  if (aqi <= 150) return { main: '#F97316', glow: 'rgba(249,115,22,0.3)', label: 'Unhealthy for Sensitive' };
  if (aqi <= 200) return { main: '#EF4444', glow: 'rgba(239,68,68,0.3)', label: 'Unhealthy' };
  return { main: '#7F1D1D', glow: 'rgba(127,29,29,0.3)', label: 'Hazardous' };
}

function getParticleColor(aqi, opacity = 1) {
  if (aqi <= 50) return `rgba(245,158,11,${opacity * 0.6})`;
  if (aqi <= 100) return `rgba(249,115,22,${opacity * 0.7})`;
  if (aqi <= 150) return `rgba(239,68,68,${opacity * 0.7})`;
  if (aqi <= 200) return `rgba(220,38,38,${opacity * 0.8})`;
  return `rgba(127,29,29,${opacity * 0.9})`;
}

// --- Extracted particle logic ---
function createOutdoorParticle(h, aqiVal) {
  return {
    x: Math.random() * 20, y: 30 + Math.random() * (h - 60),
    vx: 0.5 + Math.random() * 1.2, vy: (Math.random() - 0.5) * 0.6,
    size: 2 + Math.random() * Math.min(aqiVal / 40, 5),
    opacity: 0.3 + Math.random() * 0.6, life: 1, side: 'outdoor', absorbed: false,
  };
}

function createIndoorParticle(h, filterX) {
  return {
    x: filterX + 30 + Math.random() * 10, y: 30 + Math.random() * (h - 60),
    vx: 0.4 + Math.random() * 0.8, vy: (Math.random() - 0.5) * 0.3,
    size: 1.5 + Math.random() * 2, opacity: 0.4 + Math.random() * 0.4,
    life: 1, side: 'indoor', absorbed: false,
  };
}

function drawBackground(ctx, w, h, filterX, filterWidth, aqiVal) {
  const outdoorGrad = ctx.createLinearGradient(0, 0, filterX, 0);
  outdoorGrad.addColorStop(0, `rgba(239,68,68,${Math.min(aqiVal / 800, 0.06)})`);
  outdoorGrad.addColorStop(1, `rgba(239,68,68,${Math.min(aqiVal / 500, 0.1)})`);
  ctx.fillStyle = outdoorGrad;
  ctx.fillRect(0, 0, filterX, h);

  const indoorGrad = ctx.createLinearGradient(filterX + filterWidth, 0, w, 0);
  indoorGrad.addColorStop(0, 'rgba(16,185,129,0.04)');
  indoorGrad.addColorStop(1, 'rgba(16,185,129,0.01)');
  ctx.fillStyle = indoorGrad;
  ctx.fillRect(filterX + filterWidth, 0, w - filterX - filterWidth, h);
}

function drawFilter(ctx, h, filterX, filterWidth) {
  const filterGrad = ctx.createLinearGradient(filterX, 0, filterX + filterWidth, 0);
  filterGrad.addColorStop(0, 'rgba(63,167,214,0.15)');
  filterGrad.addColorStop(0.5, 'rgba(63,167,214,0.35)');
  filterGrad.addColorStop(1, 'rgba(63,167,214,0.15)');
  ctx.fillStyle = filterGrad;
  ctx.fillRect(filterX, 0, filterWidth, h);

  ctx.shadowColor = 'rgba(63,167,214,0.4)';
  ctx.shadowBlur = 20;
  ctx.fillStyle = 'rgba(63,167,214,0.08)';
  ctx.fillRect(filterX - 5, 0, filterWidth + 10, h);
  ctx.shadowBlur = 0;

  ctx.strokeStyle = 'rgba(63,167,214,0.3)';
  ctx.lineWidth = 1;
  for (let y = 0; y < h; y += 8) {
    ctx.beginPath();
    ctx.moveTo(filterX, y);
    ctx.lineTo(filterX + filterWidth, y);
    ctx.stroke();
  }

  ctx.save();
  ctx.translate(filterX + filterWidth / 2, h / 2);
  ctx.rotate(-Math.PI / 2);
  ctx.fillStyle = 'rgba(63,167,214,0.6)';
  ctx.font = '600 9px Inter, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('VAYU PANE', 0, 0);
  ctx.restore();
}

function updateParticle(p, filterX, aqiVal) {
  p.x += p.vx;
  p.y += p.vy;
  p.y += Math.sin(p.x * 0.02 + Date.now() * 0.001) * 0.3;

  if (p.side === 'outdoor' && p.x >= filterX - 2 && !p.absorbed) {
    if (Math.random() < 0.85) {
      p.absorbed = true;
      p.vx = 0;
      p.life -= 0.08;
    } else {
      p.side = 'passed';
      p.vx *= 0.4;
      p.size *= 0.4;
      p.opacity *= 0.3;
    }
  }

  if (p.absorbed) {
    p.opacity -= 0.04;
    p.size *= 0.97;
  }
}

function drawParticle(ctx, p, aqiVal) {
  ctx.beginPath();
  ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);

  if (p.side === 'outdoor') {
    ctx.fillStyle = getParticleColor(aqiVal, p.opacity);
  } else if (p.side === 'indoor') {
    ctx.fillStyle = `rgba(16,185,129,${p.opacity * 0.5})`;
  } else {
    ctx.fillStyle = `rgba(63,167,214,${p.opacity * 0.3})`;
  }

  if (p.absorbed) {
    ctx.fillStyle = `rgba(63,167,214,${p.opacity * 0.6})`;
    ctx.shadowColor = 'rgba(63,167,214,0.3)';
    ctx.shadowBlur = 6;
  }

  ctx.fill();
  ctx.shadowBlur = 0;
}

// --- Canvas component ---
function AirFlowCanvas({ outdoorAqi, indoorAqi, isRunning }) {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const animFrameRef = useRef(null);
  const aqiRef = useRef(outdoorAqi);
  const runningRef = useRef(isRunning);

  useEffect(() => { aqiRef.current = outdoorAqi; }, [outdoorAqi]);
  useEffect(() => { runningRef.current = isRunning; }, [isRunning]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const particles = particlesRef.current;
    let spawnTimer = 0;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener('resize', resize);

    const animate = () => {
      const rect = canvas.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;
      const filterX = w * 0.48;
      const filterWidth = w * 0.04;
      const currentAqi = aqiRef.current;

      ctx.clearRect(0, 0, w, h);
      drawBackground(ctx, w, h, filterX, filterWidth, currentAqi);
      drawFilter(ctx, h, filterX, filterWidth);

      if (runningRef.current) {
        spawnTimer++;
        const spawnRate = Math.max(2, 8 - Math.floor(currentAqi / 40));
        if (spawnTimer % spawnRate === 0) particles.push(createOutdoorParticle(h, currentAqi));
        if (spawnTimer % (spawnRate * 5) === 0) particles.push(createIndoorParticle(h, filterX));
      }

      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        updateParticle(p, filterX, currentAqi);
        if (p.opacity <= 0 || p.x > w + 10 || p.x < -10 || p.y < -10 || p.y > h + 10) {
          particles.splice(i, 1);
          continue;
        }
        drawParticle(ctx, p, currentAqi);
      }

      if (particles.length > 300) particles.splice(0, particles.length - 300);
      animFrameRef.current = requestAnimationFrame(animate);
    };

    animate();
    return () => {
      window.removeEventListener('resize', resize);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, []); // Stable deps: animation loop reads from refs

  return (
    <canvas ref={canvasRef} className="w-full rounded-2xl" style={{ height: 320, background: 'rgba(10,26,31,0.95)', border: '1px solid rgba(63,167,214,0.1)' }} data-testid="airflow-canvas" />
  );
}

// --- Main component ---
const CITIES = ['Mumbai', 'Delhi', 'Bangalore', 'Chennai', 'Kolkata', 'Hyderabad', 'Pune', 'Ahmedabad'];

export default function AirFlowSimulator() {
  const [mode, setMode] = useState('city');
  const [selectedCity, setSelectedCity] = useState('Delhi');
  const [customAqi, setCustomAqi] = useState(180);
  const [cityData, setCityData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isRunning, setIsRunning] = useState(true);

  useEffect(() => {
    if (mode !== 'city') return;
    setLoading(true);
    axios.get(`${API}/aqi/comparison?city=${selectedCity}`)
      .then(r => { setCityData(r.data); setLoading(false); })
      .catch(err => { logger.error('Simulator AQI fetch error:', err.message); setLoading(false); });
  }, [selectedCity, mode]);

  const outdoorAqi = mode === 'city' ? (cityData?.outdoor?.aqi || 0) : customAqi;
  const indoorAqi = mode === 'city' ? (cityData?.indoor?.aqi || 0) : Math.max(8, Math.round(customAqi * 0.18));
  const reduction = outdoorAqi > 0 ? Math.round((1 - indoorAqi / outdoorAqi) * 100) : 0;
  const outdoorColor = useMemo(() => getAqiColor(outdoorAqi), [outdoorAqi]);
  const indoorColor = useMemo(() => getAqiColor(indoorAqi), [indoorAqi]);

  const insightText = useMemo(() => {
    const area = mode === 'city' ? selectedCity : 'your area';
    if (outdoorAqi > 150) return <span>Outdoor air in <strong style={{ color: 'var(--text-primary)' }}>{area}</strong> is <strong style={{ color: outdoorColor.main }}>{outdoorColor.label.toLowerCase()}</strong>. Vayu Pane reduces it to <strong style={{ color: '#10B981' }}>{indoorColor.label.toLowerCase()}</strong> levels — safe to breathe.</span>;
    if (outdoorAqi > 50) return <span>Even at <strong style={{ color: outdoorColor.main }}>{outdoorColor.label.toLowerCase()}</strong> levels, Vayu Pane ensures indoor air stays at <strong style={{ color: '#10B981' }}>excellent quality</strong>.</span>;
    return <span>Air quality is currently <strong style={{ color: '#10B981' }}>good</strong>. Vayu keeps it that way, even when conditions change.</span>;
  }, [outdoorAqi, outdoorColor, indoorColor, mode, selectedCity]);

  return (
    <section className="section-padding px-6 lg:px-12" data-testid="simulator-section">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <div className="overline-teal mb-4">Interactive Simulator</div>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4" style={{ fontFamily: 'Inter' }} data-testid="simulator-title">See Vayu in action.</h2>
          <p className="text-sm max-w-lg mx-auto" style={{ color: 'var(--text-secondary)' }}>Watch polluted air get filtered in real-time. Select a city or set a custom AQI to see the difference.</p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
          <div className="flex rounded-full overflow-hidden" style={{ border: '1px solid var(--border)' }} data-testid="mode-toggle">
            <button onClick={() => setMode('city')} className="px-5 py-2 text-sm font-medium transition-all" style={{ background: mode === 'city' ? 'var(--deep-teal)' : 'transparent', color: mode === 'city' ? 'white' : 'var(--text-secondary)', fontFamily: 'Inter' }} data-testid="mode-city-btn">By City</button>
            <button onClick={() => setMode('custom')} className="px-5 py-2 text-sm font-medium transition-all" style={{ background: mode === 'custom' ? 'var(--deep-teal)' : 'transparent', color: mode === 'custom' ? 'white' : 'var(--text-secondary)', fontFamily: 'Inter' }} data-testid="mode-custom-btn">Custom AQI</button>
          </div>

          {mode === 'city' ? (
            <div className="relative" data-testid="city-dropdown">
              <select value={selectedCity} onChange={e => setSelectedCity(e.target.value)} className="appearance-none px-5 py-2 pr-10 rounded-full text-sm font-medium cursor-pointer" style={{ border: '1px solid var(--border-dark)', background: 'var(--bg-surface)', fontFamily: 'Inter', color: 'var(--text-light)' }} data-testid="simulator-city-select">
                {CITIES.map(c => <option key={c}>{c}</option>)}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: 'var(--text-muted)' }} />
            </div>
          ) : (
            <div className="flex items-center gap-3" data-testid="custom-aqi-control">
              <input type="range" min="10" max="500" value={customAqi} onChange={e => setCustomAqi(Number(e.target.value))} className="w-48 h-1.5 rounded-full appearance-none cursor-pointer" style={{ background: 'linear-gradient(to right, #10B981, #F59E0B, #EF4444, #7F1D1D)' }} data-testid="aqi-slider" />
              <span className="text-sm font-bold min-w-[50px] text-center px-3 py-1 rounded-full" style={{ background: `${getAqiColor(customAqi).main}15`, color: getAqiColor(customAqi).main, fontFamily: 'Inter' }} data-testid="aqi-slider-value">{customAqi}</span>
            </div>
          )}

          <button onClick={() => setIsRunning(r => !r)} className="px-4 py-2 rounded-full text-sm font-medium transition-all" style={{ border: '1px solid var(--border)', background: isRunning ? 'rgba(16,185,129,0.06)' : 'rgba(239,68,68,0.06)', color: isRunning ? 'var(--green-good)' : '#EF4444', fontFamily: 'Inter' }} data-testid="toggle-simulation-btn">{isRunning ? 'Running' : 'Paused'}</button>
        </div>

        <div className="relative">
          <div className="absolute top-4 left-6 z-10 flex items-center gap-2" data-testid="outdoor-zone-label">
            <div className="w-2 h-2 rounded-full" style={{ background: outdoorColor.main, boxShadow: `0 0 8px ${outdoorColor.glow}` }} />
            <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter' }}>Outdoor Air</span>
          </div>
          <div className="absolute top-4 right-6 z-10 flex items-center gap-2" data-testid="indoor-zone-label">
            <div className="w-2 h-2 rounded-full" style={{ background: '#10B981', boxShadow: '0 0 8px rgba(16,185,129,0.3)' }} />
            <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter' }}>With Vayu</span>
          </div>

          <AirFlowCanvas outdoorAqi={outdoorAqi} indoorAqi={indoorAqi} isRunning={isRunning} />

          <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between px-6 pb-5 z-10 pointer-events-none">
            <div className="rounded-xl px-5 py-3" style={{ background: 'rgba(10,26,31,0.8)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.06)' }} data-testid="outdoor-aqi-stat">
              <p className="text-[9px] uppercase tracking-wider mb-1" style={{ color: outdoorColor.main }}>{mode === 'city' ? selectedCity : 'Custom'} — Outdoor</p>
              <div className="flex items-baseline gap-1.5">
                <span className="text-2xl font-bold" style={{ fontFamily: 'Inter', color: outdoorColor.main }}>{outdoorAqi}</span>
                <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>AQI</span>
              </div>
              <p className="text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.3)' }}>{outdoorColor.label}</p>
            </div>
            <div className="rounded-full px-5 py-2.5 mb-2" style={{ background: 'linear-gradient(135deg, rgba(15,76,92,0.9), rgba(63,167,214,0.6))', backdropFilter: 'blur(8px)', border: '1px solid rgba(63,167,214,0.3)' }} data-testid="reduction-stat">
              <div className="text-center">
                <span className="text-xl font-bold text-white" style={{ fontFamily: 'Inter' }}>{reduction}%</span>
                <p className="text-[9px] uppercase tracking-wider text-white/60">filtered</p>
              </div>
            </div>
            <div className="rounded-xl px-5 py-3" style={{ background: 'rgba(10,26,31,0.8)', backdropFilter: 'blur(8px)', border: '1px solid rgba(16,185,129,0.15)' }} data-testid="indoor-aqi-stat">
              <p className="text-[9px] uppercase tracking-wider mb-1" style={{ color: '#10B981' }}>Indoor — With Vayu</p>
              <div className="flex items-baseline gap-1.5">
                <span className="text-2xl font-bold" style={{ fontFamily: 'Inter', color: '#10B981' }}>{indoorAqi}</span>
                <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>AQI</span>
              </div>
              <p className="text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.3)' }}>{indoorColor.label}</p>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{insightText}</p>
        </div>
      </div>
    </section>
  );
}
